import React, { useEffect, useRef, useState, createRef, Component } from "react";
import {stateToHTML} from 'draft-js-export-html';
import {stateFromHTML} from 'draft-js-import-html';
import Editor from "@draft-js-plugins/editor";
import createImagePlugin from "@draft-js-plugins/image";
import {
  EditorState,
  RichUtils,
  AtomicBlockUtils,
  convertToRaw,
  convertFromRaw,
} from "draft-js";

const imagePlugin = createImagePlugin();
const plugins = [imagePlugin];

const Toolbar = ({ editorState, setEditorState, onImageUploadClick }) => {
  const tools = [
    {
      label: "bold",
      style: "BOLD",
      icon: <i className="fas fa-bold" />,
      method: "inline",
    },
    {
      label: "italic",
      style: "ITALIC",
      icon: <i className="fas fa-italic"/>,
      method: "inline",
    },
    {
      label: "underline",
      style: "UNDERLINE",
      icon: <i className="fas fa-underline"/>,
      method: "inline",
    },
    {
      label: "Unordered-List",
      style: "unordered-list-item",
      method: "inline",
      icon: <i className="fas fa-list-ul"/>,
    },
    {
      label: "Ordered-List",
      style: "ordered-list-item",
      method: "block",
      icon: <i className="fas fa-list-ol" />,
    },
    // DEV NOTE ON TEXT ALIGNMENT [01-04-24]
    //  - When the align method is block, it renders properly on the front-end, but doesn't convert the property to HTML, so it never saves. Note that with this method, the page
    //   renders the css from Draft-Editor.css, as parsed by the myBlockStyleFn function below. See https://github.com/facebookarchive/draft-js/blob/main/src/component/base/DraftEditor.css
    //   Also, it seems the system must manually exclude other alignment options when one is selected, this is not a problem with the inline method.
    //  - When the align method is inline, it does neither correctly. For the front-end rendering, even though the style is specified in the styleMap object, it fails to take the whole
    //    horizontal space of the editor container, so the alignment never shows. The HTML conversion doesn't detect the alignment.
    // {
    //   label: "Left",
    //   style: "leftAlign",
    //   icon: <i className="fas fa-align-left"/>,
    //   method: "block",
    // },
    // {
    //   label: "Center",
    //   style: "centerAlign",
    //   icon: <i className="fas fa-align-center"/>,
    //   method: "block",
    // },
    // {
    //   label: "Right",
    //   style: "rightAlign",
    //   icon: <i className="fas fa-align-right"/>,
    //   method: "block",
    // },
    { label: "H1", style: "header-one", method: "block" },
    { label: "H2", style: "header-two", method: "block" },
    { label: "H3", style: "header-three", method: "block" },
  ];

  const applyStyle = (e, style, method) => {   
    e.preventDefault();
    method === "block"
      ? setEditorState(RichUtils.toggleBlockType(editorState, style))
      : setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };

  const isActive = (style, method) => {
    if (method === "block") {
      const selection = editorState.getSelection();
      const blockType = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getStartKey())
        .getType();
      return blockType === style;
    } else {
      const currentStyle = editorState.getCurrentInlineStyle();
      return currentStyle.has(style);
    }
  };

  return (
    <div className="toolbar-grid">
      {tools.map((item, idx) => (
        <button
          style={{
            color: isActive(item.style, item.method)
              ? "rgba(0, 90, 255, 0.7)"
              : "rgba(0, 0, 0, 0.3)",
          }}
          // style={{color: "rgba(0, 0, 0, 0.3)"}}
          key={`${item.label}-${idx}`}
          title={item.label}
          onClick={(e) => applyStyle(e, item.style, item.method)}
          onMouseDown={(e) => e.preventDefault()}
        >
          {item.icon || item.label}
        </button>
      ))}
      <button onClick={onImageUploadClick}><i className="fas fa-image"/></button>
    </div>
  );
};


class RichTextInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editorState: null
    };
    this.fileInputRef = createRef();
    this.editor = createRef();
    this.firstDidUpdate = false;
  }  

  componentDidMount = () => {
    this.setState({
      editorState: EditorState.createWithContent(
        stateFromHTML(this.props.value || "") 
      )
    })
  }

  componentDidUpdate = (prevProps) => {
    this.firstDidUpdate = true;
    if((prevProps.value != this.props.value) && (prevProps.value == undefined)) {
      let newState = EditorState.createWithContent(stateFromHTML(this.props.value || ""))
      newState = EditorState.moveFocusToEnd(newState)
      this.setState({
        editorState: newState
      })
    }
  }
   
  render(){    
    const {
      className,
      value,
      onChange,
      labelLocale,
      handleSaveUserImage
    } = this.props;
      
    const focusEditor = () => {
      this.editor.current.focus();
    };

    const handleKeyCommand = (command) => {
      const newState = RichUtils.handleKeyCommand(this.state.editorState, command);
      if (newState) {
        this.setState({ editorState: newState });
        return true;
      }
      return false;
    };

    // FOR INLINE STYLES
    const styleMap = {
      CODE: {
        backgroundColor: "rgba(0, 0, 0, 0.05)",
        fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
        fontSize: 16,
        padding: 2,
      },
      HIGHLIGHT: {
        backgroundColor: "#F7A5F7",
      },
      UPPERCASE: {
        textTransform: "uppercase",
      },
      LOWERCASE: {
        textTransform: "lowercase",
      },
      CODEBLOCK: {
        fontFamily: '"fira-code", "monospace"',
        fontSize: "inherit",
        background: "#ffeff0",
        fontStyle: "italic",
        lineHeight: 1.5,
        padding: "0.3rem 0.5rem",
        borderRadius: " 0.2rem",
      },
      SUPERSCRIPT: {
        verticalAlign: "super",
        fontSize: "80%",
      },
      SUBSCRIPT: {
        verticalAlign: "sub",
        fontSize: "80%",
      },
      leftAlign: {
        textAlign: "left",
      },
      rightAlign: {
        textAlign: "right",
      },
      centerAlign: {
        textAlign: "center",
      }
    };

    // FOR BLOCK LEVEL STYLES(Returns CSS Class From DraftEditor.css)
    const myBlockStyleFn = (contentBlock) => {
      const type = contentBlock.getType();
      switch (type) {
        case "blockQuote":
          return "superFancyBlockquote";
        case "leftAlign":
          return "alignLeft";
        case "rightAlign":
          return "alignRight";
        case "centerAlign":
          return "alignCenter";Ñ
        default:
          break;
      }
    };

    const setEditorState = (editorState) => {
      this.setState({ editorState });
    };

    const handleOnChangeEditor = (editorState) => {
      if(!this.firstDidUpdate) return;
      const contentState = editorState.getCurrentContent();
      let htmlValue = stateToHTML(contentState, {inlineStyles: styleMap})
      if(this.props.onChange){
        this.props.onChange({
          currentTarget: {
            dataset: {
              path: this.props["data-path"],
            },
            value: (htmlValue=="<p><br></p>") ? "" : htmlValue,
          }
        });
      }
      this.setState({ editorState: editorState })
    }

    const onImageUploadClick = () => {
      this.fileInputRef.current.click();
    }

    const handleFileChange = async (event) => {
      const file = event.target.files[0];
      if (file) {
        let files = event.target.files
        files = Object.keys(files).map(key => files[key]);
        this.fileInputRef.current.value = "";
        await handleSaveUserImage(files, insertImage.bind(this));
      }
    }
  
    const insertImage = (imageUrl) => {
      const { editorState } = this.state;
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity('IMAGE', 'IMMUTABLE', {src: imageUrl});
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const newEditorState = AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ');      

      this.setState({ editorState: newEditorState });
    }

    return (
      <div className="custom-input matter-textfield-outlined rich_text_editor_wrapper">
        <div className="rich_text_editor_label">{labelLocale || ""}</div>
        {(this.state.editorState != null) && (
          <div className="rich_text_editor_inner_wrapper" onClick={focusEditor}>
            <Toolbar
              editorState={this.state.editorState}
              setEditorState={(editorState) => { this.setState({editorState: editorState})}}              
              onImageUploadClick={onImageUploadClick}
            />
            <div className="editor-container">
              <Editor
                ref={this.editor}
                // placeholder="Write Here"
                plugins={plugins}
                handleKeyCommand={handleKeyCommand}
                editorState={this.state.editorState}
                customStyleMap={styleMap}
                blockStyleFn={myBlockStyleFn}
                onChange={handleOnChangeEditor}
              />
              <input
                type="file"
                accept="image/*"
                ref={this["fileInputRef"]}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
};

export default RichTextInput;



// WEBPACK FOOTER //
// ./source/client/components/common/inputs/richTextInput.jsx